import React, { RefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBell } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { ReactComponent as CloseIcon } from '../assets/btn_gnb_close.svg';
import { ReactComponent as MenuIcon } from '../assets/btn_gnb_menu.svg';
import { ReactComponent as MyIcon } from '../assets/btn_gnb_my.svg';
import { ReactComponent as AddMenuIcon } from '../assets/btn_menu_add.svg';
import { ReactComponent as ChatBotMenuIcon } from '../assets/btn_menu_chatbot.svg';
import { ReactComponent as HelpIcon } from '../assets/btn_menu_headset.svg';
import { ReactComponent as ListMenuIcon } from '../assets/btn_menu_list.svg';
import { ReactComponent as MyMenuIcon } from '../assets/btn_menu_my.svg';
import { ReactComponent as CoinIcon } from '../assets/ico-coin.svg';
import useAuth from '../hooks/useAuth';
import useIsMobile from '../hooks/useIsMobile';
import { useOutsideClick } from '../hooks/useOutsideClick';
import i18n from '../locales/i18n';
import { userState } from '../states/userState';
import { alertMessage } from '../utils';
import BackButton from './ui/BackButton';
import Logo from './ui/Logo';
import Text2 from './ui/Text2';

interface NavbarItemProps {
  name: string;
  path: string;
  icon: React.ReactNode;
  auth?: boolean;
}

type HeaderButtonType = 'GOBACK' | 'SHOWDRAWER';

const Navbar = ({
  type,
  title,
  showBottomLine,
  children,
}: {
  type?: HeaderButtonType;
  title?: string;
  showBottomLine?: boolean;
  children?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const { logOut } = useAuth();
  const location = useLocation();
  const drawerRef = useRef<HTMLElement>(null);
  useOutsideClick({ ref: drawerRef, handler: () => setIsOpen(false) });
  const [{ userInfo }] = useRecoilState(userState);

  const anchors: NavbarItemProps[] = [
    {
      name: t('nav.announcement'),
      path: '/announcement',
      icon: (
        <FaBell
          width={24}
          height={24}
          style={{ width: '18px', height: '21px', margin: '1.5px 5px' }}
        />
      ),
      auth: false,
    },
    {
      name: t('nav.upload'),
      path: '/upload',
      icon: <AddMenuIcon />,
      auth: false,
    },
    {
      name: t('nav.overview'),
      path: '/report',
      icon: <ListMenuIcon />,
      auth: false,
    },
    {
      name: t('nav.chatbots'),
      path: '/chatting',
      icon: <ChatBotMenuIcon />,
      auth: false,
    },
    {
      name: t('nav.customer-service'),
      path: '/help',
      icon: <HelpIcon />,
      auth: false,
    },
  ];

  const mobileAnchors: NavbarItemProps[] = [
    ...anchors,
    {
      name: t('nav.profile'),
      path: '/my-page',
      icon: <MyMenuIcon />,
      auth: true,
    },
  ];

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  const onClickLogOut = () => {
    logOut();
    alertMessage('로그아웃 되었습니다.');
    navigate('/signin', { replace: true });
  };

  const onClickLogIn = () => {
    navigate('/signin', { replace: true });
  };

  const onClickSignUp = () => {
    navigate('/signup', { replace: true });
  };

  const onClickProfile = () => {
    handleSamePath('/my-page');
    navigate('/my-page');
  };

  const onClickCredits = () => {
    handleSamePath('/charge');
    navigate('/charge');
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const handleSamePath = (path: string) => {
    if (location.pathname === path) {
      !window?.electron && window.location.reload(); // 강제 새로고침
    }
  };

  if (!isMobile) {
    return (
      <>
        <PcNav>
          <NavContentWrapper>
            <NavContentArea>
              <Logo />
              <NavItemWrapper>
                {anchors.map(
                  ({ path, name, auth }) =>
                    (userInfo || !auth) && (
                      <Link
                        key={name}
                        to={path}
                        style={{ height: '100%' }}
                        onClick={() => handleSamePath(path)}
                      >
                        <Text2
                          type="n16"
                          style={{
                            color: location.pathname.includes(path)
                              ? '#39a2ff'
                              : '#9ca5b9',
                            fontWeight: location.pathname.includes(path)
                              ? 'bold'
                              : 'normal',
                            borderBottom: location.pathname.includes(path)
                              ? '4px solid #39a2ff'
                              : 'none',
                            paddingBottom: location.pathname.includes(path)
                              ? '10px'
                              : 'none',
                            position: 'relative',
                            top: location.pathname.includes(path) ? '7px' : '0',
                          }}
                        >
                          {name}
                        </Text2>
                      </Link>
                    )
                )}
              </NavItemWrapper>
            </NavContentArea>
            <NavContentArea>
              {userInfo ? (
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  {/* <button
                  onClick={() => changeLanguage('ko')}
                  style={{ borderColor: 'red' }}
                >
                  한국어
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  style={{ borderColor: 'red' }}
                >
                  English
                </button> */}

                  <div
                    style={{
                      cursor: 'pointer',
                      marginRight: 3,
                      fontSize: 14,
                      textDecoration: location.pathname.includes('/my-page')
                        ? 'underline'
                        : 'none',
                      color: location.pathname.includes('/my-page')
                        ? '#39a2ff'
                        : '#9ca5b9',
                    }}
                    onClick={onClickProfile}
                  >
                    {userInfo.full_name}님
                  </div>
                  {userInfo.profile_image ? (
                    <ProfileImage
                      $src={userInfo.profile_image}
                      style={{ cursor: 'pointer' }}
                      onClick={onClickProfile}
                    />
                  ) : (
                    <MyIcon
                      style={{ cursor: 'pointer' }}
                      onClick={onClickProfile}
                    />
                  )}
                  <Button
                    style={{
                      marginLeft: '1px',
                      color: '#ffbf3a',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '6px 12px',
                    }}
                    onClick={onClickCredits}
                  >
                    <CoinIcon />
                    {(userInfo?.credits ?? 0).toLocaleString('ko-KR')}
                  </Button>
                  <Button style={{ marginLeft: '1px' }} onClick={onClickLogOut}>
                    {t('nav.signout')}
                  </Button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button onClick={onClickLogIn}>로그인</Button>
                  <Button onClick={onClickSignUp}>회원가입</Button>
                </div>
              )}
            </NavContentArea>
          </NavContentWrapper>
        </PcNav>
      </>
    );
  }

  return (
    <MobileNav
      style={{
        borderBottom: showBottomLine ? '1px solid #3b3f51' : 'none',
      }}
    >
      <Ul>
        <ContentWrapper $isMobile={isMobile}>
          <TitleWrapper $isMobile={isMobile}>
            {type === 'GOBACK' ? (
              <DrawerButtonWrapper>
                <BackButton />
              </DrawerButtonWrapper>
            ) : (
              <DrawerButtonWrapper onClick={toggleDrawer}>
                <MenuIcon />
              </DrawerButtonWrapper>
            )}
            {title && <Title>{title}</Title>}
            <Drawer $isOpen={isOpen} ref={drawerRef}>
              <NavBarTopArea>
                <DrawerButtonWrapper onClick={toggleDrawer}>
                  <CloseIcon />
                </DrawerButtonWrapper>
                <ProfileWrapper>
                  <Profile>
                    {userInfo?.profile_image ? (
                      <ProfileImage
                        $src={userInfo.profile_image}
                        style={{ cursor: 'pointer' }}
                        onClick={onClickProfile}
                      />
                    ) : (
                      <MyIcon
                        style={{ cursor: 'pointer' }}
                        onClick={onClickProfile}
                      />
                    )}
                    <ProfileName
                      style={{ cursor: 'pointer' }}
                      onClick={onClickProfile}
                    >
                      {userInfo?.full_name}님
                    </ProfileName>
                  </Profile>
                  <Button onClick={onClickLogOut}>{t('nav.signout')}</Button>
                </ProfileWrapper>
              </NavBarTopArea>
              {mobileAnchors.map(
                ({ path, name, icon, auth }) =>
                  (userInfo || !auth) && (
                    <Link
                      key={name}
                      to={path}
                      onClick={() => handleSamePath(path)}
                      style={{
                        display: 'flex',
                        cursor: 'pointer',
                        width: '100%',
                        height: '68px',
                        alignItems: 'center',
                        gap: '8px',
                        boxSizing: 'border-box',
                        padding: '20px 25px',
                        textDecoration: 'none',
                      }}
                    >
                      <>{icon}</>
                      <NavBarItemName>{name}</NavBarItemName>
                    </Link>
                  )
              )}
            </Drawer>
            <DrawerDimmed $isOpen={isOpen} />
            {children && (
              <div style={{ position: 'absolute', right: '15px' }}>
                {children}
              </div>
            )}
          </TitleWrapper>
        </ContentWrapper>
      </Ul>
    </MobileNav>
  );
};

export default Navbar;

// @TODO: fixed으로 변경하기
const Nav = styled.nav`
  width: 100vw;
  position: relative;
  background-color: #272a37;
  color: white;
  box-sizing: border-box;
  z-index: 100;
  /* position: fixed;
  top: 0;
  z-index: 100; */
`;

const PcNav = styled(Nav)`
  height: 80px;
  min-height: 80px;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
`;

const MobileNav = styled(Nav)`
  height: 60px;
`;

const Ul = styled.ul`
  height: 100%;
  list-style-type: 'none';
  padding: 0;
  margin-block-start: 0;
  margin-block-end: 0;
  padding-inline-start: 0;
  box-sizing: border-box;
`;

const ContentWrapper = styled.div<{ $isMobile: boolean }>`
  width: ${({ $isMobile }) => ($isMobile ? '100%' : '1140px')};
  height: 100%;
  color: white;
  margin: 0 auto;
  box-sizing: border-box;
`;

const TitleWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  height: 100%;
  font-size: 20px;
  font-weight: bold;
  font-style: italic;
  box-sizing: border-box;
  padding: ${({ $isMobile }) => ($isMobile ? '12px 15px' : '0')};
  position: relative;
`;

const Title = styled.p`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  font-style: normal;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  white-space: nowrap;
`;

const NavBarItemName = styled.span`
  font-family: Pretendard;
  font-size: 20px;
  font-weight: normal;
  font-style: normal;
  color: #fff;
`;

const NavBarTopArea = styled.div`
  width: 100%;
  box-sizing: border-box;
  height: 125px;
  margin: 0 0 10px;
  padding: 12px 20px 30px 15px;
  background-color: #272a37;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DrawerButtonWrapper = styled.div`
  cursor: pointer;
  width: 36px;
  height: 36px;
`;

const Drawer = styled.div<{ $isOpen: boolean; ref: RefObject<HTMLElement> }>`
  box-sizing: border-box;
  width: 310px;
  height: 100vh;
  background-color: #363947;
  position: absolute;
  top: 0;
  left: 0;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  z-index: 100;
  transform: translate(-100%, 0);
  animation: 0.2s showUp forwards;

  @keyframes showUp {
    0% {
      transform: translate(-100%, 0);
    }

    100% {
      transform: translate(0, 0);
    }
  }
`;

const DrawerDimmed = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 0;
  left: 0;
  z-index: 90;
`;

const ProfileWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 5px;
`;
const Profile = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const ProfileName = styled.span`
  font-family: Pretendard;
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #fff;
`;
const ProfileImage = styled.div<{ $src?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50px;
  background-color: #373a43;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: ${({ $src }) => ($src ? `url(${$src})` : 'none')};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const Button = styled.button`
  box-sizing: border-box;
  height: 32px;
  padding: 9px 22px;
  border-radius: 16px;
  border: solid 1px #9ca5b9;
  background-color: #272a37;
  cursor: pointer;

  font-family: Pretendard;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #9ca5b9;
`;

// pc
const NavContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  max-width: 1160px;
  padding: 0 30px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavContentArea = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;
`;

const NavItemWrapper = styled.ul`
  display: flex;
  align-items: center;
  gap: 50px;
`;
